import { normalizeTrialSource } from '@/shared/features/phone-verification/tracking/trialTracking';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { getSafeRedirectUrl } from '@/utils/urlValidation';

/** 복귀 목적지에 실어 보내는 발급 이용권 식별자. 게이트가 이 키를 보고 모달을 연다. */
export const ISSUED_VOUCHER_QUERY_KEY = 'issuedVoucherId';
export const TRIAL_SOURCE_QUERY_KEY = 'trialSource';

type TrialDestination =
  | string
  | {
      pathname: string;
      query?: Record<string, string | string[] | undefined>;
    };

interface ResolveTrialDestinationParams {
  callbackUrl?: string;
  defaultDestination: string;
  inviteOwnerEmail?: string | string[];
  inviteOwnerName?: string | string[];
  inviteWorkspaceId?: string | string[];
  schoolType?: string | string[];
  /**
   * 인증 완료로 발급된 이용권. 복귀한 화면에서 보상 모달을 열 신호다.
   *
   * PRD(솔루션 1·2)는 "인증 완료 후 원래 위치로 복귀 → 그 위치에서 사용 여부 확인" 순서다.
   * 모달이 페이지 이동을 건너뛰어야 하므로 목적지까지 값을 실어 보낸다.
   * localStorage 대신 query 를 쓰는 이유는 새로고침·뒤로가기에도 재현되고,
   * 앱 전역에 남아 엉뚱한 때 열리는 일이 없기 때문이다.
   */
  issuedVoucherId?: string;
  /** 체험 진입 경로. 보상 모달의 활성화 결과까지 같은 퍼널로 잇는다. */
  trialSource?: string;
}

export const resolveTrialDestination = ({
  callbackUrl,
  defaultDestination,
  inviteOwnerEmail,
  inviteOwnerName,
  inviteWorkspaceId,
  schoolType,
  issuedVoucherId,
  trialSource,
}: ResolveTrialDestinationParams): TrialDestination => {
  const normalizedTrialSource = trialSource
    ? normalizeTrialSource(trialSource)
    : undefined;

  if (inviteWorkspaceId) {
    return {
      pathname: '/workspace/join',
      query: {
        id: inviteWorkspaceId,
        name: inviteOwnerName,
        email: inviteOwnerEmail,
        ...(issuedVoucherId && {
          [ISSUED_VOUCHER_QUERY_KEY]: issuedVoucherId,
        }),
        ...(normalizedTrialSource && {
          [TRIAL_SOURCE_QUERY_KEY]: normalizedTrialSource,
        }),
      },
    };
  }

  const safeCallbackUrl = getSafeRedirectUrl(callbackUrl, defaultDestination);

  if (safeCallbackUrl === defaultDestination) {
    return {
      pathname: defaultDestination,
      ...((issuedVoucherId || normalizedTrialSource) && {
        query: {
          ...(issuedVoucherId && {
            [ISSUED_VOUCHER_QUERY_KEY]: issuedVoucherId,
          }),
          ...(normalizedTrialSource && {
            [TRIAL_SOURCE_QUERY_KEY]: normalizedTrialSource,
          }),
        },
      }),
    };
  }

  return combinePathAndQuery(safeCallbackUrl, {
    schoolType,
    ...(issuedVoucherId && { [ISSUED_VOUCHER_QUERY_KEY]: issuedVoucherId }),
    ...(normalizedTrialSource && {
      [TRIAL_SOURCE_QUERY_KEY]: normalizedTrialSource,
    }),
  });
};
